import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useLoyaltyChooseDoubleRewards from '../hooks/useLoyaltyChooseDoubleRewards'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import { mediaQueryUp } from 'components/utils/breakpoint'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import SolidCheckIcon from 'LuxKit/Icons/solid/SolidCheckIcon'
import { Fragment, useCallback, useContext, useRef, type ReactNode } from 'react'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { tierDarkBackgroundStyle, tierDarkContrastColorStyle, tierDarkForegroundBorderColorStyle, tierGradientBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'
import type { ChooseDoubleRewardsChoice } from '../utils/loyaltyChooseDoubleRewardsState'
import LoyaltyChooseDoubleRewardsLandingPageActiveLabel from './LoyaltyChooseDoubleRewardsLandingPageActiveLabel'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import { useAppSelector } from 'hooks/reduxHooks'
import useUserTierProgress from 'loyalty/hooks/useUserTierProgress'
import clsx from 'clsx'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import ModalContext from 'contexts/ModalContext'
import LoyaltyConfirmRewardChoiceModal from './LoyaltyConfirmRewardChoiceModal'
import LoyaltyPlatinumRetentionMessage from './LoyaltyPlatinumRetentionMessage'
import useLoyaltyPlatinumRetention from '../hooks/useLoyaltyPlatinumRetention'
import { LOYALTY_REWARDS_PAGE } from 'loyalty/constants/loyaltyRoutes'
import useLoyaltyChooseDoubleRewardsRegion from '../hooks/useLoyaltyChooseDoubleRewardsRegion'
import LoyaltyRewardChoiceTierProgress from './LoyaltyRewardChoiceTierProgress'
import useLoyaltyChooseDoubleRewardsTracking from '../hooks/useLoyaltyChooseDoubleRewardsTracking'

const Grid = styled.div`
  display: grid;
  gap: ${rem(20)};
  width: 100%;
  align-items: stretch;

  &.single {
    max-width: ${rem(383)};
  }

  ${mediaQueryUp.tablet} {
    &.multiple {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    &.multiple.with-or {
      grid-template-columns: minmax(0, 1fr) ${rem(40)} minmax(0, 1fr);
    }

    &.single {
      grid-template-columns: minmax(0, 1fr);
    }
  }
`

const Card = styled(Pane)`
  overflow: hidden;
  box-shadow: ${props => props.theme.shadow.bottom.medium};
  display: flex;
  flex-direction: column;

  &.active {
    border: 2px solid;
    ${tierDarkForegroundBorderColorStyle}
  }

  &.muted {
    opacity: 0.65;
    filter: grayscale(0.6);
  }
`

const Header = styled(Group)`
  padding: ${rem(16)} ${rem(24)};
  text-align: center;
  ${tierGradientBackgroundStyle}
  ${tierNormalContrastColorStyle}
`

const Content = styled(Group)`
  padding: ${rem(20)} ${rem(24)};
`

const BenefitList = styled(Group)`
  padding: 0;
  margin: 0 0 auto;
  list-style: none;
`

const OrCircle = styled(Group)`
  align-self: center;
  justify-self: center;
  width: ${rem(40)};
  height: ${rem(40)};
  border-radius: 50%;
  ${tierDarkBackgroundStyle}
  ${tierDarkContrastColorStyle}
`

function emphasis(chunks: ReactNode) {
  return (
    <strong key="emphasis">
      <em>{chunks}</em>
    </strong>
  )
}

function renderRewardSeatsLink(chunks: ReactNode) {
  return <TextLink key="reward-seats" to={`/${LOYALTY_REWARDS_PAGE}`} weight="regular" underline>{chunks}</TextLink>
}

interface Props {
  onClickSharedCta: () => void
}

function LoyaltyChooseDoubleRewardsLandingPageChoices({ onClickSharedCta }: Props) {
  const { impressionRef, track } = useLoyaltyChooseDoubleRewardsTracking('landing_choices')
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const modalOpenRef = useRef(false)
  const programName = getLoyaltyProgramDisplayName()
  const regionCode = useAppSelector(state => state.geo.currentRegionCode)
  const loggedIn = useAppSelector(selectLoggedIn)
  const { memberState, endsAt, canEnrol } = useLoyaltyChooseDoubleRewards()
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const account = useAppSelector(store => store.loyalty.account.data)
  const accountLoading = useAppSelector(store => store.loyalty.account.initial || store.loyalty.account.fetching)
  const progress = useUserTierProgress()
  const platinumRetention = useLoyaltyPlatinumRetention()
  const { needsRegionSwitch, switchRegionLabel } = useLoyaltyChooseDoubleRewardsRegion()

  const isMember = loggedIn && !!account
  const showStatusCredits = (memberState.status === 'enrolled' && memberState.choice === 'statusCredits') || tier !== 'infinite'
  const isLoading = loggedIn && accountLoading
  const hasSelectedRewardChoice = isMember && memberState.status === 'enrolled' ? memberState.choice : undefined
  const showOr = showStatusCredits && !hasSelectedRewardChoice

  const openConfirmation = useCallback(async(choice: ChooseDoubleRewardsChoice) => {
    if (modalOpenRef.current || !canEnrol) return
    track(`option_selected_${choice}`)

    modalOpenRef.current = true
    try {
      await showModal(<LoyaltyConfirmRewardChoiceModal choice={choice} />)
    } finally {
      modalOpenRef.current = false
    }
  }, [showModal, canEnrol, track])

  const onChoosePoints = useCallback(() => openConfirmation('points'), [openConfirmation])
  const onChooseStatusCredits = useCallback(() => openConfirmation('statusCredits'), [openConfirmation])

  const choices = [
    {
      key: 'points' as const,
      onClick: isMember && !needsRegionSwitch ? onChoosePoints : onClickSharedCta,
      title: intl.formatMessage(
        {
          id: 'SE4V1F1L_rfPHz38Ygka',
          defaultMessage: 'Double <emphasis>{programName} Points</emphasis>',
          description: 'Title of the points choice card on the double rewards campaign landing page',
        },
        { emphasis, programName },
      ),
      benefits: [
        {
          key: 'future-escape',
          content: intl.formatMessage({
            id: '7L3F0jjzsawT6DrKcCTG',
            defaultMessage: 'Use your points to pay for all or part of a future escape.',
            description: 'Benefit of choosing double points on the campaign landing page',
          }),
        },
        {
          key: 'reward-seats',
          content: regionCode === 'AU' ? intl.formatMessage({
            id: 'fpdDi-oq6bwjNI6MUV8A',
            defaultMessage: 'Use your points to book <link>{programName} Reward Seats</link> on our exclusive Maldives flights.',
            description: 'Reward seats benefit of choosing double points on the campaign landing page',
          }, { programName, link: renderRewardSeatsLink }) : intl.formatMessage({
            id: 'Wh4AT3vxIYxMAjkqCo6P',
            defaultMessage: 'Use your points across hotels, tours, cruises and experiences.',
            description: 'Points redemption benefit for international markets on the double rewards campaign landing page',
          }),
        },
      ],
    },
    {
      key: 'statusCredits' as const,
      onClick: isMember && !needsRegionSwitch ? onChooseStatusCredits : onClickSharedCta,
      title: intl.formatMessage(
        {
          id: 'iJ1TY4ttdHwVDcjb9sSZ',
          defaultMessage: 'Double <emphasis>Status Credits</emphasis>',
          description: 'Title of the status credits choice card on the double rewards campaign landing page',
        },
        { emphasis },
      ),
      benefits: [
        ...(tier === 'platinum' ? [] : [{
          key: 'next-tier',
          content: intl.formatMessage({
            id: 'oW11AenQLjbjq4vrdt_b',
            defaultMessage: 'Move closer to your next tier and its VIP benefits.',
            description: 'Tier progression benefit of choosing double status credits on the campaign page',
          }),
        }]),
        {
          key: 'tier-benefits',
          content: tier === 'platinum' ? intl.formatMessage({
            id: 'psaacYgHJzD0drR8wiy1',
            defaultMessage: 'Enjoy the benefits of your current tier for longer.',
            description: 'Platinum tier benefit of choosing double status credits on the campaign landing page',
          }) : intl.formatMessage({
            id: '1uanhCh0JXTLq6zvHVn2',
            defaultMessage: 'Enjoy the benefits of your current tier for longer or unlock new ones sooner.',
            description: 'Tier benefits of choosing double status credits on the campaign landing page',
          }),
        },
      ],
    },
  ]

  const visibleChoices = showStatusCredits ? choices : choices.filter(choice => choice.key !== 'statusCredits')

  const balance =
    isMember &&
    account &&
    intl.formatMessage(
      {
        id: 'S42yqZa-w1Y73pdyW-IZ',
        defaultMessage: 'Your current balance: {points, number} pts',
        description: 'Current member points balance in the double points reward choice card',
      },
      { points: account.points },
    )

  const tierProgress = isMember && !progress.isLoading && progress.hasNextTier && progress.membershipYearEndDate && progress.nextTierName ?
      <LoyaltyRewardChoiceTierProgress credits={progress.creditsNeeded} nights={progress.nightsNeeded} date={progress.membershipYearEndDate} tier={progress.nextTierName} /> :
    undefined

  const pointsActionLabel = intl.formatMessage({
    id: '_YR4pGgy7SH_RmmTUt9J',
    defaultMessage: 'Activate Double {programName} Points',
    description: 'Activation action for a member choosing double points on the campaign page',
  }, { programName })

  const creditsActionLabel = intl.formatMessage({
    id: 'gU4ysdIwK6KBimHQT7PX',
    defaultMessage: 'Activate Double Status Credits',
    description: 'Activation action for a member choosing double status credits on the campaign page',
  })

  const heading = intl.formatMessage({
    id: '5zX6QmmcXMTzqnTPSL7J',
    defaultMessage: 'Choose your double rewards',
    description: 'Heading for the choice between double points and status credits on the campaign page',
  })

  const explanationWithoutDate = intl.formatMessage({
    id: 'BVUW636V-16nxVg_fOxB',
    defaultMessage:
      'Select Double {programName} Points or Double Status Credits. You can’t change your choice after you confirm.',
    description: 'Choice explanation before the campaign booking deadline has been configured',
  }, { programName })

  const explanationWithDate = endsAt && intl.formatMessage(
    {
      id: '_NBjmX3VsHbVEE3J_9HM',
      defaultMessage:
        'Select Double {programName} Points or Double Status Credits. Your choice will apply to every eligible booking you make by {date}. You can’t change it after you confirm.',
      description: 'Explanation of the choice and booking period on the double rewards campaign page',
    },
    { programName, date: <FormatDateTime value={endsAt} day="numeric" month="long" /> },
  )

  const orLabel = intl.formatMessage({
    id: 'knKJeQB_2A59T47tTJiM',
    defaultMessage: 'or',
    description: 'Separator between the two reward choice cards on the double rewards campaign page',
  })

  const benefitsHeading = intl.formatMessage({
    id: 'hWfmi3lkt-kDo-KZHwqz',
    defaultMessage: 'Choose this option if you want to:',
    description: 'Introduction to the benefits of a reward option on the campaign landing page',
  })

  function renderActionLabel(choice: ChooseDoubleRewardsChoice) {
    if (needsRegionSwitch) return switchRegionLabel
    return choice === 'points' ? pointsActionLabel : creditsActionLabel
  }

  return (
    <Sheet id="choose-double-rewards" background="light-background" padding="48 0">
      <LayoutContainer variant="medium">
        <Group direction="vertical" gap={32} horizontalAlign="center">
          <Group direction="vertical" gap={16}>
            <Heading ref={impressionRef} variant="heading2" align="center">
              {heading}
            </Heading>
            {showStatusCredits && !endsAt && (
              <BodyText variant="medium" align="center">
                {explanationWithoutDate}
              </BodyText>
            )}
            {showStatusCredits && endsAt && (
              <BodyText variant="medium" align="center">
                {explanationWithDate}
              </BodyText>
            )}
          </Group>
          <Grid className={clsx(showStatusCredits ? 'multiple' : 'single', { 'with-or': showOr })}>
            {visibleChoices.map((choice, index) => (
              <Fragment key={choice.key}>
                {index === 1 && showOr && (
                  <OrCircle className={`tier-${tier}`} direction="horizontal" horizontalAlign="center" verticalAlign="center">
                    <BodyText variant="medium" weight="bold" colour="inherit">
                      {orLabel}
                    </BodyText>
                  </OrCircle>
                )}

                <Card
                  borderRadius="L"
                  className={clsx(`tier-${tier}`, {
                    active: hasSelectedRewardChoice === choice.key,
                    muted: !!hasSelectedRewardChoice && hasSelectedRewardChoice !== choice.key,
                  })}
                >
                  <Header direction="vertical" className={`tier-${tier}`}>
                    <Group direction="vertical" gap={4} horizontalAlign="center">
                      {hasSelectedRewardChoice === choice.key && <LoyaltyChooseDoubleRewardsLandingPageActiveLabel endsAt={endsAt} mode="until" />}
                      <Heading variant="heading3">{choice.title}</Heading>
                    </Group>
                    <BodyText variant="medium">{choice.key === 'points' ? balance : platinumRetention ? <LoyaltyPlatinumRetentionMessage {...platinumRetention} /> : tierProgress}</BodyText>
                  </Header>
                  <Content direction="vertical" gap={12} grow>
                    <BodyText variant="medium" weight="bold">
                      {benefitsHeading}
                    </BodyText>
                    <BenefitList forwardedAs="ul" direction="vertical" gap={12}>
                      {choice.benefits.map(benefit => (
                        <Group as="li" key={benefit.key} direction="horizontal" verticalAlign="start" gap={8}>
                          <SolidCheckIcon size={16} aria-hidden="true" />
                          <BodyText variant="medium">{benefit.content}</BodyText>
                        </Group>
                      ))}
                    </BenefitList>
                    {!hasSelectedRewardChoice && (
                      <TextButton
                        kind="secondary"
                        shape="round"
                        onClick={choice.onClick}
                        disabled={isLoading || (!needsRegionSwitch && isMember && !canEnrol)}
                      >
                        {renderActionLabel(choice.key)}
                      </TextButton>
                    )}
                  </Content>
                </Card>
              </Fragment>
            ))}
          </Grid>
        </Group>
      </LayoutContainer>
    </Sheet>
  )
}

export default LoyaltyChooseDoubleRewardsLandingPageChoices
